import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { CardElement } from 'react-stripe-elements';

import {
  STRIPE_OBJECT_TYPE_SOURCE,
  STRIPE_OBJECT_TYPE_TOKEN,
  STRIPE_SOURCE_TYPE_CARD,
} from 'common/stripeConstants';

import log from 'client/lib/log';
import branch from 'client/core/branch';
import withStripe from 'client/helpers/withStripe';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';

const debug = log.debug('sunroom:components:SrStripeCardForm');

@withStripe
@branch({
  currentUser: ['currentUser'],
})
class SrStripeCardForm extends Component {
  static propTypes = {
    disabled: PropTypes.bool,
    currentUser: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    submitErrorMessage: PropTypes.string,
    isSubmitting: PropTypes.bool,
    stripe: PropTypes.object.isRequired,
    buttonContainerClassName: PropTypes.string,
    buttonTitle: PropTypes.string,
    objectType: PropTypes.oneOf([
      STRIPE_OBJECT_TYPE_SOURCE,
      STRIPE_OBJECT_TYPE_TOKEN,
    ]),
    buttonHidden: PropTypes.bool,
    onReady: PropTypes.func,
  }

  static defaultProps = {
    buttonContainerClassName: '',
    buttonTitle: 'checkout',
    objectType: STRIPE_OBJECT_TYPE_SOURCE,
    buttonHidden: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      isCreatingStripeSource: false,
      createStripeSourceError: null,
    };
    this._isUnmounting = false;
  }

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  _onStripeReady = (el) => {
    const { onReady } = this.props;
    if (onReady) {
      onReady(this._onSubmit);
    }

    el.focus();
  }

  _onSubmit = (event) => {
    const { currentUser, onSubmit, onError, objectType, stripe } = this.props;

    if (event) {
      event.preventDefault();
    }

    debug(`Creating Stripe ${objectType}.`);

    this.setState({
      isCreatingStripeSource: true,
      createStripeSourceError: null,
    });

    const createStripeObjectPromise = objectType === STRIPE_OBJECT_TYPE_SOURCE
      ? (
        stripe.createSource({
          type: STRIPE_SOURCE_TYPE_CARD,
          currency: 'USD',
          owner: {
            name: currentUser.name,
          },
        })
      )
      : (
        stripe.createToken({
          name: currentUser.name,
          currency: 'USD',
        })
      );

    createStripeObjectPromise
      .then((result) => {
        let sourceOrToken;
        let error;

        if (objectType === STRIPE_OBJECT_TYPE_SOURCE) {
          sourceOrToken = result.source;
          error = result.error;
        }
        else {
          sourceOrToken = result.token;
          error = result.error;
        }

        if (!this._isUnmounting) {
          this.setState({
            isCreatingStripeSource: false,
            createStripeSourceError: null,
          });
        }
        if (error) {
          throw error;
        }

        if (!sourceOrToken) {
          const error = new Error(`stripe create ${objectType}: No response.`);
          error.name = 'SrStripeCreateSourceOrTokenNoResponseError';
          throw error;
        }

        debug(`Stripe ${objectType} received.`, sourceOrToken);
        onSubmit(sourceOrToken);
      })
      .catch((error) => {
        error.details = {
          ...(error.details || {}),
          currentUser,
        };
        log.error(error);
        this.setState({
          isCreatingStripeSource: false,
          createStripeSourceError: error,
        });
        if (onError) {
          onError(error);
        }
      });
  }

  render() {
    const {
      disabled,
      isSubmitting,
      submitErrorMessage,
      buttonContainerClassName,
      buttonTitle,
      buttonHidden,
    } = this.props;

    const { isCreatingStripeSource, createStripeSourceError } = this.state;
    const isBusy = !!(isSubmitting || isCreatingStripeSource);

    debug({
      submitErrorMessage,
      createStripeSourceError,
    });

    const errorMessage = createStripeSourceError
      ? createStripeSourceError.message || JSON.stringify(createStripeSourceError)
      : submitErrorMessage || null;

    return (
      <div className='fieldsContainer'>
        <div className='cardElementContainer'>
          <CardElement
            disabled={disabled}
            style={{
              base: {
                paddingLeft: '18px',
                height: '60px',
                width: '100%',
                fontSize: '16px',
                color: '#193340',
              },
            }}
            onReady={this._onStripeReady}
          />
          {errorMessage && (
            <div className='formMessage error'>
              <span>{errorMessage}</span>
            </div>
          )}
        </div>
        {!buttonHidden && (
          <div className={buttonContainerClassName}>
            <SrButton
              disabled={disabled || isBusy}
              type='button'
              onClick={this._onSubmit}
              theme={SR_BUTTON_THEME_ADOBE_GREEN}
            >
              {isBusy ? 'loading...' : buttonTitle}
            </SrButton>
            <span className='helperTextContainer'>
              <span className='helperText'>
                <span role='img' aria-label='lock'> 🔒</span> Securely encrypted payment
              </span>
            </span>
          </div>
        )}
      </div>
    );
  }
}

SrStripeCardForm.displayName = 'SrStripeCardForm';

export default SrStripeCardForm;
